import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { authApi, formatApiError } from "@/lib/api";
import { PublishingNav } from "@/components/publishing/PublishingShared";

const AuthShell = ({ children, subtitle }) => (
  <div className="bg-primary min-h-screen">
    <div className="noise-overlay" />
    <PublishingNav />
    <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
      <div className="max-w-md w-full">
        <Link to="/books" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors font-body text-sm mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to the Library
        </Link>
        <p className="text-jcrown-gold/70 text-[10px] tracking-[0.4em] uppercase font-body mb-3">
          Goodman Publishing
        </p>
        <p className="text-white/50 font-body text-sm mb-8">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

const Input = ({ label, testId, type = "text", value, onChange, required = true, autoComplete }) => (
  <div className="mb-5">
    <label className="block text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-2">
      {label}
    </label>
    <input
      type={type}
      required={required}
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full bg-black/40 border border-white/10 focus:border-jcrown-gold/50 text-white px-4 py-3 font-body text-sm outline-none transition-colors"
    />
  </div>
);

const SubmitButton = ({ children, loading, testId }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testId}
    className="w-full bg-jcrown-gold text-jcrown-dark px-6 py-4 font-body text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 disabled:opacity-50"
  >
    {loading ? "..." : children}
  </button>
);

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [state, setState] = useState({ loading: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "" });
    try {
      await login(form.email, form.password);
      const redirect = params.get("redirect") || "/my-library";
      navigate(redirect);
    } catch (e) {
      setState({ loading: false, error: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  return (
    <AuthShell subtitle="Sign in to your account.">
      <h1 className="font-heading text-white text-4xl md:text-5xl tracking-tight uppercase mb-10">Sign In</h1>
      <form onSubmit={submit}>
        <Input label="Email" type="email" testId="login-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
        <Input label="Password" type="password" testId="login-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" />
        {state.error && <p className="text-red-400 font-body text-sm mb-4" data-testid="login-error">{state.error}</p>}
        <SubmitButton loading={state.loading} testId="login-submit">Sign In</SubmitButton>
      </form>
      <div className="mt-8 flex flex-col sm:flex-row justify-between gap-2 text-sm">
        <Link to="/forgot-password" data-testid="link-forgot" className="text-white/50 hover:text-white transition-colors font-body">Forgot password?</Link>
        <Link to="/register" data-testid="link-register" className="text-jcrown-gold hover:text-white transition-colors font-body">Create an account →</Link>
      </div>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [state, setState] = useState({ loading: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "" });
    try {
      await register(form);
      const redirect = params.get("redirect") || "/my-library";
      navigate(redirect);
    } catch (e) {
      setState({ loading: false, error: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  return (
    <AuthShell subtitle="Create your Goodman account.">
      <h1 className="font-heading text-white text-4xl md:text-5xl tracking-tight uppercase mb-10">Sign Up</h1>
      <form onSubmit={submit}>
        <Input label="Name" testId="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required={false} autoComplete="name" />
        <Input label="Email" type="email" testId="register-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
        <Input label="Password" type="password" testId="register-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
        <p className="text-white/30 font-body text-xs mb-6">Minimum 8 characters.</p>
        {state.error && <p className="text-red-400 font-body text-sm mb-4" data-testid="register-error">{state.error}</p>}
        <SubmitButton loading={state.loading} testId="register-submit">Create Account</SubmitButton>
      </form>
      <div className="mt-8 text-center text-sm">
        <Link to="/login" data-testid="link-login" className="text-white/50 hover:text-white transition-colors font-body">Already have an account? <span className="text-jcrown-gold">Sign in</span></Link>
      </div>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, sent: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, sent: false, error: "" });
    try {
      await authApi.forgotPassword(email);
      setState({ loading: false, sent: true, error: "" });
    } catch (e) {
      setState({ loading: false, sent: false, error: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  return (
    <AuthShell subtitle="Reset your password.">
      <h1 className="font-heading text-white text-4xl md:text-5xl tracking-tight uppercase mb-10">Forgot Password</h1>
      {state.sent ? (
        <p className="text-white/70 font-body">If an account with that email exists, we've sent a reset link. Check your inbox (or the app logs in preview mode).</p>
      ) : (
        <form onSubmit={submit}>
          <Input label="Email" type="email" testId="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          {state.error && <p className="text-red-400 font-body text-sm mb-4">{state.error}</p>}
          <SubmitButton loading={state.loading} testId="forgot-submit">Send Reset Link</SubmitButton>
        </form>
      )}
      <div className="mt-8 text-center text-sm">
        <Link to="/login" className="text-white/50 hover:text-white transition-colors font-body">← Back to sign in</Link>
      </div>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [state, setState] = useState({ loading: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "" });
    try {
      await authApi.resetPassword(token, pwd);
      navigate("/login");
    } catch (e) {
      setState({ loading: false, error: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  return (
    <AuthShell subtitle="Set a new password.">
      <h1 className="font-heading text-white text-4xl md:text-5xl tracking-tight uppercase mb-10">Reset Password</h1>
      {!token ? (
        <p className="text-red-400 font-body">Missing reset token.</p>
      ) : (
        <form onSubmit={submit}>
          <Input label="New Password" type="password" testId="reset-password" value={pwd} onChange={(e) => setPwd(e.target.value)} autoComplete="new-password" />
          <p className="text-white/30 font-body text-xs mb-6">Minimum 8 characters.</p>
          {state.error && <p className="text-red-400 font-body text-sm mb-4">{state.error}</p>}
          <SubmitButton loading={state.loading} testId="reset-submit">Reset Password</SubmitButton>
        </form>
      )}
    </AuthShell>
  );
}
